import { useMutation } from "@tanstack/react-query";
import { endpoints } from "@/lib/api/endpoints";
import { apiJsonClient } from "@/lib/query/core/api-json-client";
import type { WalletProviderValue } from "../constants/wallet-providers";

export type GenerateSetupInput = {
  businessType: string;
  walletPayment?: {
    wallets: Array<{
      provider: WalletProviderValue;
      phoneNumber: string;
    }>;
  };
  instaPayPayment?: {
    receiverType: "PHONE" | "IPA" | "BANK_ACCOUNT";
    phoneNumber?: string;
    ipaHandle?: string;
    bankName?: string;
    accountHolderName?: string;
    accountNumber?: string;
  };
  bankTransferPayment?: {
    bankName: string;
    accountHolderName: string;
    accountNumber: string;
    branchName?: string;
  };
  acceptsCOD: boolean;
  shippingRules?: Array<{
    governorateIds: string[];
    price: number;
    estimatedDeliveryDays: number;
  }>;
  shippingGovernorates: string[];
  shippingPrice: number;
  estimatedDeliveryDays: number;
  wantsDeposit: boolean;
  depositType?: "percent" | "flat";
  depositAmount?: number;
  enabledPolicies?: ("SHIPPING" | "REFUND" | "EXCHANGE")[];
};

export type GeneratedStoreSetup = {
  categories: Array<{ nameAr: string; nameEn: string }>;
  storeDescription: string;
  storeDescriptionEn: string;
  storeHighlights: Array<{ textAr: string; textEn?: string }>;
  paymentOptions: Array<{
    type: "WALLET" | "INSTAPAY" | "BANK" | "OTHER";
    entries: Array<{ labelAr: string; labelEn: string; value: string }>;
  }>;
  shippingRules: Array<{
    name?: string;
    estimatedDeliveryDays: number;
    price: number;
    governorateIds: string[];
  }>;
  storeSettings: {
    depositType?: "percent" | "flat";
    depositAmount?: number;
    includeShippingInOrderDeposit: boolean;
    whatsappNumber: string;
  };
  acceptsCOD: boolean;
  storePolicies?: Array<{
    policyType: "SHIPPING" | "REFUND" | "EXCHANGE";
    isEnabled: boolean;
    contentAr: string;
    contentEn: string;
  }>;
};

export function useGenerateSetupMutation() {
  const mutation = useMutation({
    meta: { skipGlobalErrorToast: true },
    mutationFn: (input: GenerateSetupInput) =>
      apiJsonClient<GeneratedStoreSetup>(endpoints.seller.onboardingGenerate, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      }),
  });

  return {
    mutateAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
    error: mutation.error,
    data: mutation.data,
    reset: mutation.reset,
  };
}
